import { Component, OnInit } from '@angular/core';
import { DataService } from '../../../core/services/data.service';
import { LocationService } from '../../../core/services/location.service';
import { Enterprise, Domain, Sector, SubSector, Pharmacy, Institution, Advertisement } from '../../../core/models/business.model';
import { finalize, forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { LicenceService } from '../../../core/services/licence.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LicenceModalComponent } from 'src/app/shared/licence-modal/licence-modal.component';

// Range Slider
import { grid } from './grid.model';
import { gridData } from './data';
import { Options } from 'ngx-slider-v2';

// Définir une interface pour les marqueurs
interface CustomMapMarker {
  position: google.maps.LatLngLiteral;
  title: string;
  label: google.maps.MarkerLabel;
}

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss']
})

/**
 * Grid Component
 */
export class GridComponent implements OnInit {

  // bread crumb items
  breadCrumbItems!: Array<{}>;
  gridData!: grid[];
  longitude = 20.728218;
  latitude = 52.128973;
  dataCount = 0;
  term: any;

  // Données
  enterprises: Enterprise[] = [];
  domains: Domain[] = [];
  sectors: Sector[] = [];
  subSectors: SubSector[] = [];

  // Pagination
  currentPage = 1;
  isLoadingMore = false;
  hasMoreData = true;

  // Filtres
  selectedDomain?: number;
  selectedSector?: number;
  selectedSubSector?: number;
  searchQuery = '';

  // États
  isLoading = true;
  error: string | null = null;

  // Mapping des catégories vers les IDs de domaine
  private categoryMap: { [key: string]: number } = {
    'Services': 1,
    'Industrie': 2,
    'Commerce': 3,
    'Batiment': 4,
    'Artisanat': 5,
    'Agriculture': 6,
    'Institutions': 7,
    'Pharmacies': 8
  };

  // Ajout des propriétés pour la localisation
  selectedLocation?: string;
  selectedDistrict?: string;

  // Liste des villes
  cities = [
    'NIAMEY',
    'ZINDER',
    'MARADI',
    'TAHOUA',
    'AGADEZ',
    'DOSSO',
    'DIFFA',
    'TILLABERI'
  ];

  // Liste des quartiers par ville
  districts: { [key: string]: string[] } = {
    "NIAMEY": [
      "Plateau",
      "Yantala",
      "Lazaret",
      "Terminus",
      "Kalley",
      "Goudel",
      "Kombo",
      "Hamdallaye"
    ],
    "ZINDER": [
      "Birni",
      "Garin Malam",
      "Kara Kara",
      "Fadin",
      "Gaya",
      "Magaria"
    ],
    "MARADI": [
      "Centre-ville",
      "Gossolol",
      "Tiguidanke",
      "Bagala",
      "Alaba",
      "Wadata"
    ],
    "AGADEZ": [
      "Agadez Centre",
      "Assaki",
      "Tchittagou",
      "In-Gall"
    ],
    "TAHOUA": [
      "Tahoua Centre",
      "Tera",
      "Bouza",
      "Dawani"
    ],
    "DIFFA": [
      "Diffa Centre",
      "Fachi",
      "Bosso",
      "Mao"
    ],
    "DOSSO": [
      "Dosso Centre",
      "Gouré",
      "Loga",
      "Madaoua"
    ],
    "TILLABERI": [
      "Tillabéri Centre",
      "Ouallam",
      "Say",
      "Filingue",
      "Kankara"
    ]
  };

  // Quartiers actuellement disponibles basés sur la ville sélectionnée
  availableDistricts: string[] = [];

  // Propriétés pour le slider de prix
  minValue: number = 0;
  maxValue: number = 1000;
  options: any = {
    floor: 0,
    ceil: 1000,
    translate: (value: number): string => {
      return '$' + value;
    }
  };

  // Type de données actuellement affiché
  currentDataType: 'enterprises' | 'pharmacies' | 'institutions' = 'enterprises';

  // Données pour chaque type
  allEnterprises: Enterprise[] = [];
  allPharmacies: Pharmacy[] = [];
  allInstitutions: Institution[] = [];

  // Constante pour la taille de page
  private readonly PAGE_SIZE = 50;

  // Ajouter une propriété pour stocker les données filtrées
  private filteredData: any[] = [];

  // Propriété pour gérer l'état du toggle
  showGardeFilter: boolean = false;
  filterGarde: boolean = false; // État du toggle pour filtrer par garde

  // Options de la carte
  mapOptions: google.maps.MapOptions = {
    center: { lat: 13.5137, lng: 2.1098 }, // Coordonnées de Niamey
    zoom: 12,
    mapTypeId: 'roadmap'
  };

  // Marqueurs pour les entreprises/pharmacies/institutions
  markers: CustomMapMarker[] = [];

  advertisements: Advertisement[] = [];

  // Ajouter une propriété pour stocker le type en attente
  private pendingType: string | null = null;

  showWhatsAppOptions: boolean = false;

  hasAccess: boolean = false;

  selectedCity: string = '';

  constructor(
    private dataService: DataService,
    private locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private licenceService: LicenceService,
    private modalService: NgbModal
  ) {
    this.licenceService.accessState$.subscribe(state => {
      this.hasAccess = state.entreprises;
    });
  }

  ngOnInit(): void {
    /**
     * BreadCrumb
     */
    this.breadCrumbItems = [
      { label: 'Accueil', link: '' },
      { label: 'Trouver une entreprise', active: true }
    ];

    this.loadInitialData();

    document.body.classList.remove('bg-secondary');

    // Écouter les changements de paramètres de route
    this.route.queryParams.subscribe(params => {
      const type = params['type'];
      if (type) {

        // Convertir le type en minuscules pour la comparaison
        const normalizedType = type.toLowerCase();
        // Attendre que les domaines soient chargés avant de procéder
        if (this.domains.length > 0) {
          // Trouver le domaine correspondant
          const domain = this.domains.find(d => d.libelle.toLowerCase() === normalizedType);
          if (domain) {
            // Utiliser onDomainChange pour gérer la sélection du domaine
            this.onDomainChange(domain.id.toString());
          }
        } else {
          // Si les domaines ne sont pas encore chargés, stocker le type pour plus tard
          this.pendingType = normalizedType;
        }


        // Réinitialiser les filtres
        this.selectedSector = undefined;
        this.selectedSubSector = undefined;
        this.sectors = [];
        this.subSectors = [];

        // Recharger les données
        this.loadEnterprises();
      }
      if (params['q']) {
        this.searchQuery = params['q'];
        this.onSearch(this.searchQuery);
      }
      if (params['city']) {
        this.selectedCity = params['city'];
        // Ajoutez ici la logique pour filtrer par ville si nécessaire
      }
    });

    // Charger les publicités
    this.loadAdvertisements();
  }

  private loadInitialData(): void {
    this.isLoading = true;

    this.dataService.getDomains().subscribe({
      next: (domains) => {
        this.domains = domains;

        // Vérifier s'il y a un type en attente à traiter
        if (this.pendingType) {
          const domain = this.domains.find(d => d.libelle.toLowerCase() === this.pendingType);
          if (domain) {
            this.onDomainChange(domain.id.toString());
          }
          this.pendingType = null;
        }

        // Charger toutes les données initiales
        this.loadAllData();
      },
      error: (error) => {
        this.error = 'Erreur lors du chargement des domaines';
        console.error('Erreur:', error);
      }
    });
  }

  private loadAllData(): void {
    // Charger toutes les données en parallèle
    forkJoin({
      enterprises: this.dataService.getEnterprises(),
      pharmacies: this.dataService.getPharmacies(),
      institutions: this.dataService.getInstitutions()
    }).subscribe({
      next: (data) => {
        this.allEnterprises = data.enterprises;
        this.allPharmacies = data.pharmacies;
        this.allInstitutions = data.institutions;
        this.loadEnterprises();
      },
      error: (error) => {
        this.error = 'Erreur lors du chargement des données';
        console.error('Erreur:', error);
      }
    });
  }

  loadEnterprises(page: number = 1): void {
    this.isLoadingMore = true;

    // Sélectionner la source de données appropriée
    let sourceData: any[] = [];
    switch (this.currentDataType) {
      case 'pharmacies':
        sourceData = this.allPharmacies;
        break;
      case 'institutions':
        sourceData = this.allInstitutions;
        break;
      default:
        sourceData = this.allEnterprises;
    }

    // Appliquer les filtres sur toutes les données
    this.filteredData = [...sourceData];

    // Filtre de recherche
    if (this.searchQuery) {
      this.filteredData = this.filteredData.filter(item => {
        return (
          (item.nom?.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (item.adresse?.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (item.region?.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
          (item.telephone?.toLowerCase().includes(this.searchQuery.toLowerCase()))
        );
      });
    }

    // Filtre de ville
    if (this.selectedCity) {
      this.filteredData = this.filteredData.filter(item => 
        item.region?.toLowerCase() === this.selectedCity.toLowerCase()
      );
    }

    // Autres filtres existants...
    if (this.filterGarde) {
      this.filteredData = this.filteredData.filter(item => item.garde === 'oui');
    }

    if (this.selectedLocation) {
      this.filteredData = this.filteredData.filter(e => e.region === this.selectedLocation);
    }

    if (this.selectedDistrict) {
      this.filteredData = this.filteredData.filter(e => e.adresse === this.selectedDistrict);
    }

    if (this.selectedSubSector) {
      this.filteredData = this.filteredData.filter(e => e.sousecteur_id === this.selectedSubSector);
    }

    // Mettre à jour le nombre total de résultats
    this.dataCount = this.filteredData.length;

    // Vérifier s'il y a plus de données
    const totalPages = Math.ceil(this.filteredData.length / this.PAGE_SIZE);
    this.hasMoreData = page < totalPages;

    // Pagination
    const start = (page - 1) * this.PAGE_SIZE;
    const end = start + this.PAGE_SIZE;
    const paginatedData = this.filteredData.slice(start, end);

    // Calculer les distances
    paginatedData.forEach(item => {
      this.locationService.calculateEnterpriseDistance(item)
        .subscribe(distance => {
          item.distance = distance;
        });
    });

    // Mettre à jour les données affichées
    if (page === 1) {
      this.enterprises = paginatedData;
    } else {
      this.enterprises = [...this.enterprises, ...paginatedData];
    }

    this.currentPage = page;
    this.isLoadingMore = false;
    this.isLoading = false;
  }

  onDomainChange(domainId: string): void {
    this.selectedDomain = domainId ? parseInt(domainId, 10) : undefined;
    this.selectedSector = undefined;
    this.selectedSubSector = undefined;
    this.sectors = [];
    this.subSectors = [];

    if (this.selectedDomain) {
      const domain = this.domains.find(d => d.id === this.selectedDomain);
      if (domain) {
        const domainName = domain.libelle.toLowerCase();
        this.currentDataType = domainName as typeof this.currentDataType;

        // Mettre à jour l'URL sans recharger la page
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { type: domainName },
          queryParamsHandling: 'merge'
        });

        // Charger les secteurs si ce n'est pas une pharmacie ou une institution
        if (!['pharmacies', 'institutions'].includes(domainName)) {
          this.dataService.getSectorsByDomain(this.selectedDomain).subscribe({
            next: (sectors) => {
              this.sectors = sectors;
            }
          });
        }
      }
    } else {
      this.currentDataType = 'enterprises';
      // Supprimer le paramètre type de l'URL
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { type: null },
        queryParamsHandling: 'merge'
      });
    }

    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  onSectorChange(sectorId: string): void {
    this.selectedSector = sectorId ? parseInt(sectorId, 10) : undefined;
    this.selectedSubSector = undefined;
    this.subSectors = [];

    if (this.selectedSector) {
      this.dataService.getSubSectors(this.selectedSector).subscribe({
        next: (subSectors) => {
          this.subSectors = subSectors;
          this.currentPage = 1;
          this.hasMoreData = true;
          this.loadEnterprises();
        }
      });
    } else {
      this.loadEnterprises();
    }
  }

  onSubSectorChange(subSectorId: string): void {
    this.selectedSubSector = subSectorId ? parseInt(subSectorId, 10) : undefined;
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  loadMore(): void {
    if (this.hasMoreData && !this.isLoadingMore) {
      this.loadEnterprises(this.currentPage + 1);
    }
  }

  /**
   * Filter button clicked
   */
  FilterSidebar() {
    document.getElementById('filters-sidebar')?.classList.toggle('show');
    document.querySelector('.vertical-overlay')?.classList.toggle('show');
  }

  /**
   * SidebarHide modal
   */
  SidebarHide() {
    document.getElementById('filters-sidebar')?.classList.remove('show');
    document.querySelector('.vertical-overlay')?.classList.remove('show');
  }

  // Map Model Open
  openMapModal() {
    this.initializeMap();
    document.querySelector('.map-popup')?.classList.remove('invisible');
  }

  // Map Model Close
  closeMapModel() {
    document.querySelector('.map-popup')?.classList.add('invisible');
  }

  // Sort filter
  sortField: any;
  sortBy: any;
  SortFilter() {
    this.sortField = (document.getElementById("sortby") as HTMLInputElement).value;
    if (this.sortField[0] == 'A') {
      this.sortBy = 'desc';
      this.sortField = this.sortField.replace(/A/g, '')
    }
    if (this.sortField[0] == 'D') {
      this.sortBy = 'asc';
      this.sortField = this.sortField.replace(/D/g, '')
    }
    // Recharger les données avec le nouveau tri
    this.loadEnterprises();
  }

  categoryClick(event: Event, categoryName: string): void {
    event.preventDefault();
    // Trouver le domaine correspondant au nom de la catégorie
    const domain = this.domains.find(d => d.libelle.toLowerCase() === categoryName.toLowerCase());
    if (domain) {
      this.selectedDomain = domain.id;
      this.selectedSector = undefined;
      this.selectedSubSector = undefined;

      // Charger les secteurs du domaine sélectionné
      this.dataService.getSectorsByDomain(domain.id).subscribe({
        next: (sectors) => {
          this.sectors = sectors;
          this.subSectors = [];
          this.currentPage = 1;
          this.hasMoreData = true;
          this.loadEnterprises();
        }
      });
    }
  }

  onLocationChange(location: string): void {
    this.selectedLocation = location || undefined;
    this.selectedDistrict = undefined;
    this.availableDistricts = location ? (this.districts[location] || []) : [];
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  onDistrictChange(district: string): void {
    this.selectedDistrict = district || undefined;
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  // Méthodes pour les filtres
  categoryDownClick(event: Event): void {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const categoryText = target.querySelector('.dropdown-item-label')?.textContent;
    if (categoryText) {
      this.categoryClick(event, categoryText);
    }
  }


  valueChange(value: number, isMin: boolean): void {
    if (isMin) {
      this.minValue = value;
    } else {
      this.maxValue = value;
    }
    // Appliquer le filtre de prix
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadEnterprises();
  }

  onSearchInput(searchValue: string): void {
    this.searchQuery = searchValue.toLowerCase().trim();

    // Sélectionner la source de données appropriée
    let sourceData: any[] = [];
    switch (this.currentDataType) {
      case 'pharmacies':
        sourceData = this.allPharmacies;
        break;
      case 'institutions':
        sourceData = this.allInstitutions;
        break;
      default:
        sourceData = this.allEnterprises;
    }

    // Appliquer les filtres sur toutes les données
    this.filteredData = [...sourceData];

    // Filtre de recherche
    if (this.searchQuery) {
      this.filteredData = this.filteredData.filter(item => {
        return (
          (item.nom?.toLowerCase().includes(this.searchQuery)) ||
          (item.adresse?.toLowerCase().includes(this.searchQuery)) ||
          (item.region?.toLowerCase().includes(this.searchQuery)) ||
          (item.telephone?.toLowerCase().includes(this.searchQuery))
        );
      });
    }

    // Appliquer les autres filtres existants
    if (this.selectedLocation) {
      this.filteredData = this.filteredData.filter(e => e.region === this.selectedLocation);
    }

    if (this.selectedDistrict) {
      this.filteredData = this.filteredData.filter(e => e.adresse === this.selectedDistrict);
    }

    if (this.currentDataType === 'enterprises' && this.selectedSubSector) {
      this.filteredData = this.filteredData.filter(e => e.sousecteur_id === this.selectedSubSector);
    }

    // Mettre à jour le compteur et la pagination
    this.dataCount = this.filteredData.length;
    this.currentPage = 1;

    // Paginer les résultats
    const paginatedData = this.filteredData.slice(0, this.PAGE_SIZE);
    this.hasMoreData = this.filteredData.length > this.PAGE_SIZE;

    // Mettre à jour l'affichage
    this.enterprises = paginatedData;
    this.isLoading = false;
  }

  onGardeToggleChange(): void {
    this.currentPage = 1; // Réinitialiser la page
    this.hasMoreData = true; // Réinitialiser l'état de chargement

    // Filtrer les pharmacies en fonction de l'état du toggle
    this.loadEnterprises();
  }

  // Méthode pour initialiser la carte avec les marqueurs
  initializeMap(): void {
    // Utiliser this.enterprises qui contient les éléments actuellement affichés
    if (this.enterprises.length > 0) {
      this.markers = this.enterprises
        .filter(e => e.latitude !== null && e.longitude !== null)
        .map(e => ({
          position: {
            lat: Number(e.latitude),
            lng: Number(e.longitude)
          },
          title: e.nom || '',
          label: {
            text: e.nom || '',
            color: 'black'
          }
        }));

      // Centrer la carte sur le premier marqueur
      if (this.markers.length > 0) {
        this.mapOptions = {
          ...this.mapOptions,
          center: this.markers[0].position,
          zoom: 12
        };
      }
    }
  }

  // Méthode pour centrer la carte sur un marqueur
  centerMap(enterprise: Enterprise): void {
    if (enterprise.latitude !== null && enterprise.longitude !== null) {
      this.mapOptions = {
        ...this.mapOptions,
        center: {
          lat: Number(enterprise.latitude),
          lng: Number(enterprise.longitude)
        },
        zoom: 15
      };
    }
  }

  private loadAdvertisements(): void {
    this.dataService.getAdvertisements().subscribe({
      next: (ads) => {
        this.advertisements = ads;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des publicités:', error);
      }
    });
  }

  // Méthode pour obtenir le titre de recherche dynamique
  getSearchTitle(): string {
    switch (this.currentDataType) {
      case 'pharmacies':
        return 'Rechercher une pharmacie';
      case 'institutions':
        return 'Rechercher une institution';
      default:
        return 'Rechercher une entreprise';
    }
  }

  openLicenceModal() {
    const modalRef = this.modalService.open(LicenceModalComponent);
    // You can pass data to the modal if needed
    // modalRef.componentInstance.someProperty = someValue;
  }
}
